/* eslint-disable @guusje4525/sp-assignment-check */
import BaseCacheStore from "cache/BaseCacheStore"
import { apiClient } from "api/trpc/trpc-client"
import WebSocketEventAdvisorBlueprintListUpdate from "../websockets/models/WebSocketEventAdvisorBlueprintListUpdate"
import BlueprintMinimalDto from "../app-services/blueprint/models/BlueprintMinimalDto"
import { notNull } from "@gap/utils/utils"
import { BlueprintVersionDto } from "../app-services/blueprint/models/BlueprintVersionDto"
import variables from "../variables/variables"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { UserOrgDto } from "../app-services/user-org/models/UserOrgDto"

export interface BlueprintToClone {
   blueprintId: string
   blueprintOrgId: string
   newBlueprintName: string
}

interface AdvisorBlueprintCacheStoreProps {
   orgGuardStore: OrgGuardStore
}

export default class AdvisorBlueprintCacheStore extends BaseCacheStore<BlueprintMinimalDto, "blueprintId"> {
   private sp: AdvisorBlueprintCacheStoreProps

   constructor(sp: AdvisorBlueprintCacheStoreProps) {
      super(AdvisorBlueprintCacheStore.getBaseCacheStoreProps(sp.orgGuardStore.userOrg))
      this.sp = sp
   }

   get publishedGapBlueprints(): BlueprintMinimalDto[] {
      return this.allItems.filter(bp => bp.thisBlueprintPublishedData?.orgId === variables.gapOrgId && bp.status === "published")
   }

   /** Get all Advisor blueprints */
   get advisorBlueprints(): BlueprintMinimalDto[] {
      return this.allItems.filter(bp => bp.thisBlueprintPublishedData?.orgId !== variables.gapOrgId)
   }

   get publishedAdvisorBlueprints(): BlueprintMinimalDto[] {
      return this.allItems.filter(bp => bp.thisBlueprintPublishedData?.orgId !== variables.gapOrgId && bp.status === "published")
   }

   get publishedGapAndAdvisorBlueprints(): BlueprintMinimalDto[] {
      return this.allItems.filter(bp => bp.status === "published")
   }

   get archivedAdvisorBlueprints(): BlueprintMinimalDto[] {
      return this.allItems.filter(bp => bp.thisBlueprintPublishedData?.orgId !== variables.gapOrgId && bp.status === "archived")
   }

   get nonArchivedAdvisorBlueprints(): BlueprintMinimalDto[] {
      const publishedBlueprintsToRemove = new Set<string>()

      // First, identify published blueprints that have an active draft (these need to be removed from the list)
      this.advisorBlueprints.forEach(bp => {
         if (bp.status === "draft" && bp.thisBlueprintPublishedData) {
            publishedBlueprintsToRemove.add(bp.thisBlueprintPublishedData.blueprintId)
         }
      })

      // Second, filter non-archived blueprints and exclude those published ones with drafts
      const blueprints = this.advisorBlueprints.filter(bp =>
         bp.status !== "archived" &&
         !publishedBlueprintsToRemove.has(bp.blueprintId)
      )

      return blueprints
   }

   private static getBaseCacheStoreProps(userOrg: UserOrgDto.Info) {
      if (!userOrg.isDeactivated) {
         return {
            fetchAll: apiClient.blueprint.getAdvisorAndPublishedGapBlueprints.query,
            fetchOne: async (id: string, orgId?: string): Promise<BlueprintMinimalDto | undefined> => apiClient.blueprint.getBlueprint.query({ blueprintId: id, blueprintOrgId: notNull(orgId, "orgId is required in AdvisorBlueprintCacheStore") }),
            key: "blueprintId" as const
         }
      } else {
         return {
            fetchAll: async () => ([]),
            fetchOne: async () => (undefined),
            key: "blueprintId" as const
         }
      }
   }

   /** WebSocket Handler */
   handleBlueprintEvent(event: WebSocketEventAdvisorBlueprintListUpdate) {
      const { action, blueprint } = event

      if (!blueprint) {
         console.warn(`Received WebSocket event "${action}" without a valid blueprint object.`)
         return
      }

      switch (action) {
         case 'add':
         case 'duplicate':
         case 'restore':
         case 'archive':
            this.setItem(blueprint)
            break

         case 'publish':
            this.updateBlueprintToPublished(blueprint)
            break

         case 'update':
            this.setItem(blueprint)
            break

         case 'delete':
            this.removeItem(blueprint.blueprintId)
            break

         default:
            console.warn(`Unhandled WebSocket event: ${action}`)
      }
   }


   publishBlueprint = async (draftBlueprint: BlueprintMinimalDto) => {
      const { blueprintId, orgId } = draftBlueprint
      const existingDraft = this.getItem(blueprintId)

      if (!existingDraft) {
         return
      }

      const previouslyPublishedBlueprintId = existingDraft.thisBlueprintPublishedData?.blueprintId
      const lastUpdated = new Date().toISOString()

      const versionData: BlueprintVersionDto = {
         blueprintId,
         blueprintOrgId: existingDraft.orgId,
         isFirstVersion: false,
         versionSummary: `Published on ${lastUpdated}`,
         previousVersionProductionReady: false,
         versionType: "minor",
         versionNumber: undefined,
         previousVersionNumber: undefined
      }

      await apiClient.blueprint.publish.mutate(versionData)

      // Remove the old published version (if exists)
      if (previouslyPublishedBlueprintId) {
         this.removeItem(previouslyPublishedBlueprintId)
      }

      // Update the cache with the newly published blueprint
      this.setItem({
         ...existingDraft,
         name: draftBlueprint.name,
         lastUpdated,
         thisBlueprintOpenDraftData: undefined, // Remove draft info
         thisBlueprintPublishedData: { orgId, blueprintId }, // Mark as published
         status: "published",
      })
   }

   cloneBlueprint = async ({ blueprintId, blueprintOrgId, newBlueprintName }: BlueprintToClone) => {
      const clonedBlueprint = await apiClient.blueprint.clone.mutate({
         newOrgId: notNull(this.sp.orgGuardStore.orgId, `orgId not initialized before cloning`),
         blueprintId,
         blueprintOrgId,
         newName: newBlueprintName,
      })

      this.setItem(clonedBlueprint)

      return clonedBlueprint
   }

   /**
    * Creates a draft from a blueprint
    * @param blueprint blueprint to create a draft from
    */
   createDraft = async (blueprint: BlueprintMinimalDto): Promise<BlueprintMinimalDto> => {
      const draft = await apiClient.blueprint.createDraft.mutate({ blueprintId: blueprint.blueprintId, blueprintOrgId: blueprint.orgId, lockedEntityId: blueprint.parentVersionId })
      this.setItem(draft)
      return draft
   }

   setDraftChanges = (blueprint: BlueprintMinimalDto) => {
      this.setItem(blueprint)
   }

   discardDraft = async (blueprint: BlueprintMinimalDto) => {
      const { blueprintId, orgId, thisBlueprintPublishedData } = blueprint

      await apiClient.blueprint.discardDraftBlueprint.mutate({ blueprintId, blueprintOrgId: orgId })

      // Remove draft from cache
      this.removeItem(blueprintId)

      // If there's a published version, update its cache to remove draft reference
      if (thisBlueprintPublishedData) {
         const publishedBlueprint = this.getItem(thisBlueprintPublishedData.blueprintId)
         if (publishedBlueprint) {
            this.setItem({
               ...publishedBlueprint,
               thisBlueprintOpenDraftData: undefined, // Clear draft reference
            })
         }
      }
   }

   archiveBlueprint = async (blueprint: BlueprintMinimalDto) => {
      const existingBlueprint = this.getItem(blueprint.blueprintId)
      if (!existingBlueprint) return

      await apiClient.blueprint.archive.mutate({ blueprintId: blueprint.blueprintId, blueprintOrgId: blueprint.orgId })
      this.setItem({
         ...existingBlueprint,
         status: 'archived',
         thisBlueprintPublishedData: undefined,
         thisBlueprintArchivedData: { orgId: existingBlueprint.orgId, blueprintId: blueprint.blueprintId }
      })
   }

   restoreBlueprint = async (blueprint: BlueprintMinimalDto) => {
      const existingBlueprint = this.getItem(blueprint.blueprintId)
      if (!existingBlueprint) return

      await apiClient.blueprint.restore.mutate({ blueprintId: blueprint.blueprintId, blueprintOrgId: blueprint.orgId })
      this.setItem({
         ...existingBlueprint,
         status: 'published',
         thisBlueprintArchivedData: undefined,
         thisBlueprintPublishedData: { orgId: existingBlueprint.orgId, blueprintId: blueprint.blueprintId }
      })
   }

   private updateBlueprintToPublished(blueprint: BlueprintMinimalDto) {
      const { blueprintId, orgId, thisBlueprintPublishedData } = blueprint

      const existingDraft = this.getItem(blueprintId)
      if (!existingDraft) return

      // Remove previously published version if exists
      if (thisBlueprintPublishedData?.blueprintId) {
         this.removeItem(thisBlueprintPublishedData.blueprintId)
      }

      this.setItem({
         ...existingDraft,
         name: blueprint.name,
         lastUpdated: blueprint.lastUpdated,
         lastUpdatedByUserName: blueprint.lastUpdatedByUserName,
         thisBlueprintOpenDraftData: undefined, // Remove draft info
         thisBlueprintPublishedData: { orgId, blueprintId }, // Mark as published
         status: 'published',
      })
   }
}
